import { AuthType } from '../config/apiDataSourceEnums';

const MAX_REF_DEPTH = 3;

/**
 * Resolve a JSON $ref pointer within the spec.
 * Handles #/components/schemas/X (OpenAPI 3.x) and #/definitions/X (Swagger 2.0).
 */
function resolveRef(spec, ref, depth = 0) {
  if (!ref || typeof ref !== 'string' || depth >= MAX_REF_DEPTH) return {};
  const parts = ref.replace(/^#\//, '').split('/');
  let current = spec;
  for (const part of parts) {
    current = current?.[part];
    if (!current) return {};
  }
  if (current.$ref) {
    return resolveRef(spec, current.$ref, depth + 1);
  }
  return current;
}

/**
 * If the schema (or resolved ref) has a $ref, resolve it; otherwise return as-is.
 */
function resolveSchema(spec, schema, depth = 0) {
  if (!schema || depth >= MAX_REF_DEPTH) return schema || {};
  if (schema.$ref) return resolveRef(spec, schema.$ref, depth);
  return schema;
}

/**
 * Generate a JSON skeleton object from a schema definition.
 * Produces default values: string→"", number/integer→0, boolean→false.
 */
function generateSkeleton(schema, spec, depth = 0) {
  if (!schema || depth >= MAX_REF_DEPTH) return null;
  const resolved = resolveSchema(spec, schema, depth);

  switch (resolved.type) {
    case 'string':
      return resolved.example ?? resolved.default ?? '';
    case 'integer':
    case 'number':
      return resolved.example ?? resolved.default ?? 0;
    case 'boolean':
      return resolved.example ?? resolved.default ?? false;
    case 'array': {
      const itemSkeleton = resolved.items
        ? generateSkeleton(resolved.items, spec, depth + 1)
        : null;
      return itemSkeleton !== null ? [itemSkeleton] : [];
    }
    case 'object':
    default: {
      if (resolved.properties) {
        const obj = {};
        for (const [key, propSchema] of Object.entries(resolved.properties)) {
          const val = generateSkeleton(propSchema, spec, depth + 1);
          obj[key] = val !== null ? val : '';
        }
        return obj;
      }
      if (resolved.type === 'object') return {};
      // Unknown / unresolvable
      return resolved.example ?? resolved.default ?? null;
    }
  }
}

/**
 * Detect whether the spec is OpenAPI 3.x or Swagger 2.0.
 */
function detectVersion(spec) {
  if (spec.openapi && spec.openapi.startsWith('3.')) return '3.x';
  if (spec.swagger === '2.0') return '2.0';
  return null;
}

/**
 * Build the base URL from the spec.
 */
function getBaseUrl(spec, version) {
  if (version === '3.x') {
    const server = spec.servers?.[0];
    if (!server) return '';
    return (server.url || '').replace(/\/+$/, '');
  }
  // Swagger 2.0
  const scheme = spec.schemes?.[0] || 'https';
  const host = spec.host || '';
  const basePath = (spec.basePath || '').replace(/\/+$/, '');
  if (!host) return basePath;
  return `${scheme}://${host}${basePath}`;
}

/**
 * Get the security schemes map from the spec.
 */
function getSecuritySchemes(spec, version) {
  if (version === '3.x') return spec.components?.securitySchemes || {};
  return spec.securityDefinitions || {};
}

/**
 * Map an OpenAPI security scheme to our AuthType config.
 * Returns a partial auth config object, or null if not mappable.
 */
function mapSecurityScheme(scheme) {
  if (!scheme) return null;
  const type = scheme.type?.toLowerCase();

  if (type === 'apikey' && (scheme.in === 'header' || !scheme.in)) {
    return {
      authType: AuthType.ApiKey,
      apiKeyHeaderName: scheme.name || 'X-API-Key',
      apiKeyValue: '',
    };
  }
  if (type === 'http') {
    if (scheme.scheme?.toLowerCase() === 'bearer') {
      return { authType: AuthType.BearerToken, bearerToken: '' };
    }
    if (scheme.scheme?.toLowerCase() === 'basic') {
      return { authType: AuthType.BasicAuth, username: '', password: '' };
    }
  }
  if (type === 'oauth2') {
    const flows = scheme.flows || {};
    const ccFlow = flows.clientCredentials || flows.application;
    if (ccFlow) {
      return {
        authType: AuthType.OAuth2ClientCredentials,
        tokenUrl: ccFlow.tokenUrl || '',
        clientId: '',
        clientSecret: '',
        scope: Object.keys(ccFlow.scopes || {}).join(' '),
      };
    }
    // Swagger 2.0 uses flow field directly
    if (scheme.flow === 'application' || scheme.flow === 'clientCredentials') {
      return {
        authType: AuthType.OAuth2ClientCredentials,
        tokenUrl: scheme.tokenUrl || '',
        clientId: '',
        clientSecret: '',
        scope: Object.keys(scheme.scopes || {}).join(' '),
      };
    }
  }
  return null;
}

/**
 * Guess the response data path from the success response schema.
 * Looks for an array-typed property (common pattern: { data: [...] }).
 */
function guessResponseDataPath(spec, operation, version) {
  let schema = null;

  if (version === '3.x') {
    const resp = operation.responses?.['200'] || operation.responses?.['201']
      || operation.responses?.default;
    if (!resp) return '';
    const content = resp.content;
    if (!content) return '';
    const jsonContent = content['application/json'] || content['*/*'];
    schema = jsonContent?.schema;
  } else {
    const resp = operation.responses?.['200'] || operation.responses?.['201']
      || operation.responses?.default;
    schema = resp?.schema;
  }

  if (!schema) return '';
  const resolved = resolveSchema(spec, schema);

  // If root is already an array, no path needed
  if (resolved.type === 'array') return '';

  // Look for array properties in the object
  if (resolved.properties) {
    for (const [key, propSchema] of Object.entries(resolved.properties)) {
      const propResolved = resolveSchema(spec, propSchema);
      if (propResolved.type === 'array') return key;
    }
    // Check nested: common patterns like data.items, response.records
    for (const [key, propSchema] of Object.entries(resolved.properties)) {
      const propResolved = resolveSchema(spec, propSchema);
      if (propResolved.properties) {
        for (const [subKey, subSchema] of Object.entries(propResolved.properties)) {
          const subResolved = resolveSchema(spec, subSchema);
          if (subResolved.type === 'array') return `${key}.${subKey}`;
        }
      }
    }
  }

  return '';
}

/**
 * Extract the request body schema for POST operations.
 */
function getRequestBodySchema(spec, operation, version) {
  if (version === '3.x') {
    const content = operation.requestBody?.content;
    if (!content) return null;
    const jsonContent = content['application/json'] || content['*/*'];
    return jsonContent?.schema || null;
  }
  // Swagger 2.0: body params
  const bodyParam = (operation.parameters || []).find(p => p.in === 'body');
  return bodyParam?.schema || null;
}

/**
 * Check if the operation consumes/produces JSON.
 */
function hasJsonContentType(spec, operation, version) {
  if (version === '3.x') {
    const content = operation.requestBody?.content;
    return content && !!content['application/json'];
  }
  const consumes = operation.consumes || spec.consumes || [];
  return consumes.includes('application/json');
}

/**
 * Parse an OpenAPI/Swagger spec and extract available endpoints.
 *
 * @param {Object} spec - Parsed JSON of the OpenAPI/Swagger document.
 * @returns {{ version: string, title: string, description: string, baseUrl: string, endpoints: Array }}
 * @throws {Error} if the spec is not a valid OpenAPI/Swagger document.
 */
export function parseOpenApiSpec(spec) {
  if (!spec || typeof spec !== 'object') {
    throw new Error('Invalid specification: expected a JSON object.');
  }

  const version = detectVersion(spec);
  if (!version) {
    throw new Error(
      'Not a valid OpenAPI/Swagger specification. Missing "openapi" or "swagger" field.'
    );
  }

  const title = spec.info?.title || 'Untitled API';
  const description = spec.info?.description || '';
  const baseUrl = getBaseUrl(spec, version);
  const supportedMethods = ['get', 'post'];
  const endpoints = [];

  const paths = spec.paths || {};
  for (const [path, pathItem] of Object.entries(paths)) {
    for (const method of supportedMethods) {
      const operation = pathItem?.[method];
      if (!operation) continue;

      endpoints.push({
        path,
        method: method.toUpperCase(),
        summary: operation.summary || '',
        operationId: operation.operationId || '',
        tags: operation.tags || [],
        description: operation.description || '',
        hasRequestBody: method === 'post' && !!getRequestBodySchema(spec, operation, version),
        deprecated: !!operation.deprecated,
      });
    }
  }

  return { version, title, description, baseUrl, endpoints };
}

/**
 * Extract a wizard-compatible config patch from a selected endpoint.
 *
 * @param {Object} spec - The full parsed OpenAPI/Swagger spec.
 * @param {{ path: string, method: string }} endpoint - Selected endpoint.
 * @returns {Object} Config patch matching wizardConfig shape.
 */
export function extractConfigFromEndpoint(spec, endpoint) {
  const version = detectVersion(spec);
  const baseUrl = getBaseUrl(spec, version);
  const operation = spec.paths?.[endpoint.path]?.[endpoint.method.toLowerCase()];
  if (!operation) return {};

  const name = operation.summary || operation.operationId || `${endpoint.method} ${endpoint.path}`;
  const url = `${baseUrl}${endpoint.path}`;

  // Request body skeleton
  let requestBody = '';
  if (endpoint.method === 'POST') {
    const bodySchema = getRequestBodySchema(spec, operation, version);
    if (bodySchema) {
      const skeleton = generateSkeleton(bodySchema, spec);
      if (skeleton !== null) {
        try {
          requestBody = JSON.stringify(skeleton, null, 2);
        } catch {
          requestBody = '';
        }
      }
    }
  }

  // Response data path
  const responseDataPath = guessResponseDataPath(spec, operation, version);

  // Headers
  const headers = [];
  if (endpoint.method === 'POST' && hasJsonContentType(spec, operation, version)) {
    headers.push({ key: 'Content-Type', value: 'application/json' });
  }

  // Auth config
  let auth = { authType: AuthType.None };
  const securitySchemes = getSecuritySchemes(spec, version);
  const securityReqs = operation.security || spec.security || [];

  if (securityReqs.length > 0 && Object.keys(securitySchemes).length > 0) {
    // Use the first security requirement that we can map
    for (const req of securityReqs) {
      const schemeName = Object.keys(req)[0];
      const scheme = securitySchemes[schemeName];
      const mapped = mapSecurityScheme(scheme);
      if (mapped) {
        auth = { ...auth, ...mapped };
        break;
      }
    }
  } else if (Object.keys(securitySchemes).length > 0) {
    // No explicit security on the operation, try global schemes
    const firstScheme = Object.values(securitySchemes)[0];
    const mapped = mapSecurityScheme(firstScheme);
    if (mapped) {
      auth = { ...auth, ...mapped };
    }
  }

  return {
    name,
    url,
    httpMethod: endpoint.method,
    requestBody,
    responseDataPath,
    headers,
    auth,
  };
}
